import React from 'react';
import { BusinessIdea, ComparisonAnalysis } from '@/src/types';
import { useReporting } from '@/src/hooks/useReporting';
import { useAuth } from '@/src/contexts/AuthContext';
import { LoadingStates } from '../hooks/useVentureIntelligence';
import DisclaimerTooltip from './DisclaimerTooltip';

interface Props {
  savedIdeas: BusinessIdea[];
  onClose: () => void;
  onSelectIdea: (idea: BusinessIdea) => void;
  preferredCurrency?: string;
  comparisonData: ComparisonAnalysis | null;
  loadingStates: LoadingStates;
  onRunComparison: (activeIdeas: BusinessIdea[], preferredCurrency: string) => Promise<ComparisonAnalysis | undefined>;
  onSetComparisonData: (data: ComparisonAnalysis | null) => void;
}

const MetricGauge: React.FC<{ value: number; label: string; color: string }> = ({ value, label, color }) => {
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-12 h-12 flex items-center justify-center">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            className="text-white/10"
          />
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="square"
            className={color}
          />
        </svg>
        <span className="absolute text-[8px] font-bold font-heading text-white">{value}%</span>
      </div>
      <span className="text-[7px] font-bold uppercase tracking-[0.2em] text-white/40 font-heading text-center leading-none">{label}</span>
    </div>
  );
};

const CompareView: React.FC<Props> = ({ 
  savedIdeas, 
  onClose, 
  onSelectIdea, 
  preferredCurrency = 'USD', 
  comparisonData,
  loadingStates,
  onRunComparison,
}) => {
  const { isLoggedIn, onSignUpPrompt } = useAuth();
  const { exportBusinessSnapshot, exportComparisonReport } = useReporting();
  const [selectedTitles, setSelectedTitles] = React.useState<string[]>(() => {
    if (!savedIdeas) return [];
    return savedIdeas.map(i => i?.title).filter(Boolean) as string[];
  });

  const activeIdeas = savedIdeas.filter(i => selectedTitles.includes(i.title));

  const renderKeys = new Set<string>();
  const validateAndGetUniqueKey = (id: string | number, idx: number, prefix: string): string => {
    let key = `${prefix}-${id}-${idx}`;
    if (renderKeys.has(key)) {
      let suffix = 1;
      while (renderKeys.has(`${key}-${suffix}`)) {
        suffix++;
      }
      key = `${key}-${suffix}`;
    }
    renderKeys.add(key);
    return key;
  };

  const toggleSelection = (title: string) => {
    setSelectedTitles(prev => 
      prev.includes(title) 
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const handleRunComparison = async () => {
    if (activeIdeas.length < 2) return;
    try {
      await onRunComparison(activeIdeas, preferredCurrency);
    } catch (error) {
      console.error('Comparison error:', error);
      alert('Strategic analysis failed. Please check your connection and try again.');
    }
  };

  const handleExportComparedIntel = () => {
    if (!comparisonData) {
      alert('Please run AI Comparison first to generate the printable competitive matrix.');
      return;
    }
    if (!isLoggedIn) {
      onSignUpPrompt();
      return;
    }
    exportComparisonReport(activeIdeas, comparisonData);
  };

  const isDiscoveryHub = savedIdeas.length > 0 && savedIdeas[0].hubOrigin === 'discovery';

  return (
    <div className="fixed inset-0 z-[120] bg-[#322D29] flex flex-col animate-slide-up text-white selection:bg-[#F26522]/30">
      <header className="px-8 py-5 flex items-center justify-between border-b border-white/[0.06] bg-[#322D29] sticky top-0 z-[130]">
        <div className="flex flex-col gap-0.5">
          <span className={`text-[10px] font-bold uppercase tracking-[0.25em] font-heading ${isDiscoveryHub ? 'text-emerald-400' : 'text-[#F26522]'}`}>
            {isDiscoveryHub ? 'Regional Recon Room' : 'Tactical War Room'}
          </span>
          <h2 className="text-xl font-bold tracking-tight font-heading text-white">
            {isDiscoveryHub ? 'Compare Recon' : 'Compare Intelligence'}
          </h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex flex-col items-end mr-4">
            <span className="text-[8px] font-bold uppercase tracking-widest text-white/40">Active Nodes</span>
            <span className="text-base font-bold text-white font-heading">{activeIdeas.length} / {savedIdeas.length}</span>
          </div>
          <button 
            onClick={onClose} 
            className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-all border border-white/10 group cursor-pointer"
            aria-label="Close Compare View"
          >
            <i className="fas fa-xmark text-sm group-hover:rotate-90 transition-transform"></i>
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar bg-[#322D29]">
        {/* AI Comparison Results Section */}
        {comparisonData && (
          <div className="px-8 py-8 bg-[#3a3430] border-b border-white/[0.06] animate-fade-in">
            <div className="w-full space-y-8 max-w-6xl mx-auto">
              <div className="flex flex-col gap-1">
                <span className={`text-[9px] font-bold uppercase tracking-[0.25em] ${isDiscoveryHub ? 'text-emerald-400' : 'text-[#F26522]'}`}>
                  {isDiscoveryHub ? 'Recon Intelligence Briefing' : 'AI Strategic Briefing'}
                </span>
                <h3 className="text-xl font-bold tracking-tight font-heading text-white">Comparative Analysis</h3>
                <p className="text-sm font-medium text-white/70 leading-relaxed max-w-none font-dm">{comparisonData.summary}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-5 bg-[#322D29] border border-white/[0.06] rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-3.5 bg-rose-500 rounded-sm"></div>
                    <span className="text-[9px] font-bold text-white/60 uppercase tracking-widest font-heading">Critical Gaps</span>
                  </div>
                  <ul className="space-y-2.5">
                    {comparisonData.gaps.map((gap, i) => {
                      if (!gap) return null;
                      const uniqueKey = validateAndGetUniqueKey(gap, i, 'gap-item');
                      return (
                      <li key={uniqueKey} className="flex gap-3 items-start text-xs font-medium text-white/80 font-dm">
                        <span className="w-5 h-5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-400 flex items-center justify-center shrink-0 text-[9px] font-bold">{i + 1}</span>
                        <span>{gap}</span>
                      </li>
                    );
                    })}
                  </ul>
                </div>

                <div className="space-y-4 p-5 bg-[#322D29] border border-white/[0.06] rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-3.5 bg-amber-500 rounded-sm"></div>
                    <span className="text-[9px] font-bold text-white/60 uppercase tracking-widest font-heading">Comparative Risks</span>
                  </div>
                  <ul className="space-y-2.5">
                    {comparisonData.risks.map((risk, i) => {
                      if (!risk) return null;
                      const uniqueKey = validateAndGetUniqueKey(risk, i, 'risk-item');
                      return (
                      <li key={uniqueKey} className="flex gap-3 items-start text-xs font-medium text-white/80 font-dm">
                        <span className="w-5 h-5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center justify-center shrink-0 text-[9px] font-bold">{i + 1}</span>
                        <span>{risk}</span>
                      </li>
                    );
                    })}
                  </ul>
                </div>
              </div>

              <div className="p-6 bg-[#322D29] border border-white/[0.06] rounded-xl text-white space-y-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
                  <i className={`fas ${isDiscoveryHub ? 'fa-satellite' : 'fa-trophy'} text-5xl ${isDiscoveryHub ? 'text-emerald-500' : 'text-[#F26522]'}`}></i>
                </div>
                <div className="space-y-1">
                  <span className={`text-[9px] font-bold uppercase tracking-widest ${isDiscoveryHub ? 'text-emerald-400' : 'text-[#F26522]'}`}>
                    {isDiscoveryHub ? 'Top Recon Match' : 'Strategic Selection'}
                  </span>
                  <h4 className="text-xl font-bold font-heading text-white">{comparisonData.winningVenture}</h4>
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Selection Reasoning</span>
                  <p className="text-xs font-medium text-white/70 leading-relaxed max-w-none font-dm">{comparisonData.selectionReasoning}</p>
                </div>
              </div>

              <div className="p-6 bg-[#322D29] border border-white/[0.06] rounded-xl space-y-2">
                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest font-heading">Final Recommendation</span>
                <p className="text-sm font-bold font-heading leading-relaxed text-white">{comparisonData.recommendation}</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-3.5 bg-[#F26522] rounded-sm"></div>
                  <span className="text-[9px] font-bold text-white/60 uppercase tracking-widest font-heading">Comparison Matrix</span>
                </div>
                <div className="overflow-x-auto border border-white/[0.06] rounded-xl">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#322D29] border-b border-white/[0.06]">
                        <th className="p-4 text-[9px] font-bold uppercase tracking-widest text-white/40 border-r border-white/[0.06] font-heading">Feature</th>
                        {activeIdeas.map((idea, idx) => {
                          if (!idea || (!idea.id && !idea.title)) return null;
                          const uniqueKey = validateAndGetUniqueKey(idea.id || idea.title, idx, 'header');
                          return (
                          <th key={uniqueKey} className="p-4 text-[9px] font-bold uppercase tracking-widest text-white font-heading">{idea.title}</th>
                        );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.comparisonMatrix.map((row, rIdx) => {
                        if (!row || !row.feature) return null;
                        const rowUniqueKey = validateAndGetUniqueKey(row.feature, rIdx, 'row');
                        return (
                        <tr key={rowUniqueKey} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                          <td className="p-4 text-[10px] font-bold uppercase tracking-widest text-white/50 bg-[#322D29]/50 border-r border-white/[0.06] font-heading">{row.feature}</td>
                          {activeIdeas.map((idea, idx) => {
                            if (!idea || (!idea.id && !idea.title)) return null;
                            const cellUniqueKey = validateAndGetUniqueKey(`${idea.id || idea.title}-${row.feature}`, idx, 'cell');
                            return (
                            <td key={cellUniqueKey} className="p-4 text-xs font-medium text-white/80 font-dm">{row.values[idea.title] || row.values[Object.keys(row.values)[0]] || 'N/A'}</td>
                          );
                          })}
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-8 w-full max-w-7xl mx-auto">
          {savedIdeas.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-24 gap-6">
               <div className="w-16 h-16 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center text-white/40">
                 <i className="fas fa-satellite-dish text-2xl"></i>
               </div>
               <div className="space-y-1">
                 <p className="text-base font-bold uppercase font-heading tracking-widest text-white">No Saved Opportunities</p>
                 <p className="text-[10px] font-medium uppercase tracking-widest text-white/40">Save opportunities to begin comparison</p>
               </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {savedIdeas.map((idea, idx) => {
                if (!idea || (!idea.id && !idea.title)) return null;
                const isSelected = selectedTitles.includes(idea.title);
                const isDiscoveryFlow = idea.hubOrigin === 'discovery';
                const ideaUniqueKey = validateAndGetUniqueKey(idea.id || idea.title, idx, 'idea');
                
                return (
                  <div 
                    key={ideaUniqueKey} 
                    onClick={() => toggleSelection(idea.title)}
                    className={`group bg-[#3a3430] border rounded-xl flex flex-col relative transition-all cursor-pointer ${
                      isSelected ? 'border-[#F26522] ring-1 ring-[#F26522]' : 'border-white/[0.06] hover:border-white/20'
                    }`}
                  >
                    {/* Checkbox Overlay */}
                    <div className="absolute top-5 left-5 z-20">
                      <div className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${
                        isSelected ? 'bg-[#F26522] border-[#F26522]' : 'bg-[#322D29] border-white/20 group-hover:border-white/40'
                      }`}>
                        {isSelected && <i className="fas fa-check text-white text-xs"></i>}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 space-y-4 flex-1 pl-15">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <span className={`text-[8px] font-bold uppercase tracking-widest block font-heading ${isDiscoveryFlow ? 'text-emerald-400' : 'text-[#F26522]'}`}>
                            {isDiscoveryFlow ? 'Regional Domain' : idea.category}
                          </span>
                          <h3 className="text-base font-bold text-white font-heading leading-tight group-hover:text-[#F26522] transition-colors">
                            {idea.title}
                          </h3>
                        </div>
                        {!isDiscoveryFlow && (
                          <div className="flex gap-2.5">
                            <MetricGauge value={idea.marketDemandScore || 85} label="Demand" color="text-[#F26522]" />
                            <MetricGauge value={idea.saturationIndex || 30} label="Gap" color="text-emerald-400" />
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-white/60 font-medium leading-relaxed italic border-l-2 border-white/10 pl-3 line-clamp-2 font-dm">
                        "{idea.description}"
                      </p>

                      {/* Tactical Sections */}
                      {!isDiscoveryFlow && (
                        <div className="grid grid-cols-2 gap-3 border-t border-white/[0.06] pt-3">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">Launch Math</span>
                            <DisclaimerTooltip>
                              <p className="text-[10px] font-bold text-white font-dm">ROI: {idea.potentialROI || 'Calculating...'}</p>
                            </DisclaimerTooltip>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">Risk Guard</span>
                            <DisclaimerTooltip>
                              <p className="text-[10px] font-bold text-white font-dm">Resilience: {idea.riskGuard?.score || idea.riskLevel || 'Medium'}</p>
                            </DisclaimerTooltip>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading mb-0.5">
                            {isDiscoveryFlow ? 'Identity Intensity' : 'Start Capital'}
                          </span>
                          <DisclaimerTooltip>
                            <span className={`text-base font-bold font-heading ${isDiscoveryFlow ? 'text-emerald-400' : 'text-white'}`}>
                              {isDiscoveryFlow ? (idea.densityInsight || 'Verified Hallmark') : (idea.estimatedStartupCost || '$--')}
                            </span>
                          </DisclaimerTooltip>
                        </div>
                        
                        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                          <button 
                            onClick={() => onSelectIdea(idea)}
                            className="w-7 h-7 bg-white/5 hover:bg-white/10 hover:text-white rounded-lg flex items-center justify-center text-white/50 transition-all border border-white/10 cursor-pointer"
                            title="View Dossier"
                          >
                            <i className="fas fa-eye text-[9px]"></i>
                          </button>
                          <button 
                            onClick={() => {
                              if (!isLoggedIn) {
                                onSignUpPrompt();
                                return;
                              }
                              exportBusinessSnapshot(idea);
                            }}
                            className="w-7 h-7 bg-white/5 hover:bg-white/10 hover:text-white rounded-lg flex items-center justify-center text-white/50 transition-all border border-white/10 cursor-pointer"
                            title="Download PDF"
                          >
                            <i className="fas fa-download text-[9px]"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <footer className="px-8 py-4 border-t border-white/[0.06] bg-[#322D29] flex flex-row gap-3 items-center w-full justify-start z-40">
        <button 
          onClick={handleRunComparison}
          disabled={activeIdeas.length < 2 || loadingStates.isComparing}
          className={`text-white py-2.5 px-5 rounded-lg font-bold uppercase text-[10px] tracking-widest transition-all font-heading disabled:opacity-30 active:scale-[0.98] flex items-center justify-center gap-2 shrink-0 cursor-pointer ${
            isDiscoveryHub ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#F26522] hover:bg-[#F26522]/90'
          }`}
        >
          {loadingStates.isComparing ? (
            <i className="fas fa-circle-notch fa-spin text-xs"></i>
          ) : (
            <i className="fas fa-brain text-xs"></i>
          )}
          Compare Selected Intel
        </button>
        
        <button 
          onClick={handleExportComparedIntel}
          disabled={activeIdeas.length < 2 || !comparisonData}
          className="bg-white/10 hover:bg-white/15 text-white py-2.5 px-5 rounded-lg font-bold uppercase text-[10px] tracking-widest border border-white/10 transition-all font-heading disabled:opacity-30 active:scale-[0.98] flex items-center justify-center gap-2 shrink-0 cursor-pointer"
          title="Export Only Compared Intel Data"
        >
          <i className="fas fa-file-pdf text-xs"></i>
          Export Compared Intel
        </button>

        <button 
          onClick={onClose}
          className="border border-white/10 text-white/70 hover:text-white py-2.5 px-4 rounded-lg font-bold uppercase text-[10px] tracking-widest hover:bg-white/5 transition-all font-heading active:scale-[0.98] flex items-center justify-center gap-2 shrink-0 cursor-pointer"
          title="Return to Home"
        >
          <i className="fas fa-home text-xs"></i>
          Home
        </button>
      </footer>
    </div>
  );
};

export default CompareView;
